import { useApolloClient, useMutation, useReactiveVar } from '@apollo/client';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  cart_item_added,
  cart_item_quantity_changed,
  cart_item_removed,
  discount_applied,
  promo_code_applied,
  promo_code_failed,
} from '@/analytics';
import { useCurrent } from '@/hooks/useCurrent';
import {
  AddItemToCartInput,
  CartItemType,
  ICartItem,
  IOrderSummary,
  UpdateItemQuantityInput,
} from '@/imports/ui/components/Cart/TypeDefs';
import {
  ADD_CART_ITEM,
  APPLY_PROMOCODE,
  CLEAR_CART,
  REMOVE_CART_ITEM,
  REMOVE_PROMOCODE,
  UPDATE_CART_ITEM,
} from '@/imports/ui/components/Cart/graphql/mutations';
import { GET_CART } from '@/imports/ui/components/Cart/graphql/queries';

import { cartActions, cartVar } from './cartState';

const getCartItemAnalyticsProps = (
  item: Record<string, any>,
  quantity?: number
) => {
  const itemType = String(item.itemType || item.type || '');
  const isEventItem = itemType === CartItemType.EVENTS || Boolean(item.eventId);
  const isMembershipItem =
    itemType === CartItemType.MEMBERSHIP || Boolean(item.membershipOrderId);

  return {
    entity_id:
      item.eventId ?? item.membershipOrderId ?? item.itemId ?? item._id,
    entity_type: isEventItem
      ? 'event'
      : isMembershipItem
      ? 'membership'
      : 'cart_item',
    event_id: item.eventId ?? undefined,
    event_name: item.eventName ?? item.name,
    membership_order_id: item.membershipOrderId,
    membership_payment_interval:
      item.membershipOrderCache?.paymentInterval ?? item.paymentInterval,
    membership_renewal_setting:
      item.membershipOrderCache?.renewalSetting ?? item.renewalSetting,
    membership_is_renewal:
      item.membershipOrderCache?.isRenewal ?? item.isRenewal,
    ticket_id: item.eventTicketId,
    cart_item_id: item._id || item.itemId,
    cart_item_type: itemType,
    cart_item_name: item.name,
    cart_quantity: quantity ?? item.quantity,
    cart_price: item.unitPrice,
    cart_total_amount: item.total,
    payment_item_type: isEventItem
      ? 'event_ticket'
      : isMembershipItem
      ? 'membership'
      : itemType,
  };
};

export interface UseCartReturn {
  // State
  items: ICartItem[];
  orderSummary: IOrderSummary | null;
  itemCount: number;
  total: number;
  loading: boolean;
  error: string | null;
  isEmpty: boolean;
  appliedPromoCode: string | null;
  subscriptionConnected: boolean;

  // Actions
  addToCart: (items: AddItemToCartInput[]) => Promise<void>;
  removeFromCart: (cartId: string) => Promise<void>;
  updateQuantity: (input: UpdateItemQuantityInput) => Promise<void>;
  clearCart: () => Promise<void>;
  applyPromoCode: (code: string) => Promise<void>;
  refreshCart: () => Promise<void>;

  // Utilities
  getItemById: (id: string) => ICartItem | undefined;
  getTotalQuantity: () => number;
}

export const useCart = (): UseCartReturn => {
  const apolloClient = useApolloClient();
  const { user, loading: userLoading } = useCurrent();
  const cartState = useReactiveVar(cartVar);
  const initialLoadAttempted = useRef(false);
  const [hasAttemptedInitialLoad, setHasAttemptedInitialLoad] = useState(false);

  // GraphQL operations
  const [addItemMutation] = useMutation(ADD_CART_ITEM);
  const [removeItemMutation] = useMutation(REMOVE_CART_ITEM);
  const [updateQuantityMutation] = useMutation(UPDATE_CART_ITEM);
  const [clearCartMutation] = useMutation(CLEAR_CART);
  const [applyPromoMutation] = useMutation(APPLY_PROMOCODE);
  const [removePromoMutation] = useMutation(REMOVE_PROMOCODE);

  // NOTE: Cart subscription is now initialized in CartInitializer component
  // at the root level to persist across route changes. We don't call it here
  // to avoid multiple subscription instances.

  // Define refreshCart function early so it can be used in effects
  const refreshCart = useCallback(async () => {
    if (!user?._id) return;

    try {
      cartActions.setLoading(true);

      apolloClient.cache.evict({ fieldName: 'getCart' });
      apolloClient.cache.gc();

      const { data } = await apolloClient.query({
        query: GET_CART,
        variables: { input: {} },
        fetchPolicy: 'network-only',
      });

      if (data?.getCart?.success && data.getCart.result) {
        cartActions.updateCartFromResponse(data.getCart.result);
      } else {
        cartActions.setLoading(false);
      }
    } catch (error) {
      cartActions.setError(
        error instanceof Error ? error.message : 'Failed to refresh cart'
      );
      cartActions.setLoading(false);
    }
  }, [apolloClient, user?._id]);

  useEffect(() => {
    // If user is available and we haven't loaded cart yet, start loading
    if (
      user?._id &&
      !initialLoadAttempted.current &&
      cartState.items.length === 0 &&
      !cartState.loading
    ) {
      initialLoadAttempted.current = true;
      setHasAttemptedInitialLoad(true);
      // Set loading immediately to prevent showing empty state during initial load
      cartActions.setLoading(true);
      refreshCart();
    }
    // If user becomes available and we haven't attempted load, mark as attempted
    // This prevents showing empty state while waiting for user to load
    else if (
      user?._id &&
      !hasAttemptedInitialLoad &&
      cartState.items.length === 0
    ) {
      setHasAttemptedInitialLoad(true);
    }
  }, [
    user?._id,
    cartState.items.length,
    cartState.loading,
    hasAttemptedInitialLoad,
    refreshCart,
  ]);

  // Actions
  const addToCart = useCallback(
    async (items: AddItemToCartInput[]) => {
      try {
        cartActions.setLoading(true);

        const { data } = await addItemMutation({
          variables: { input: items },
        });

        if (data?.addItemToCart?.success) {
          items.forEach((item) => {
            try {
              const props = getCartItemAnalyticsProps(item);
              cart_item_added(props);
            } catch (error) {
              console.error('[Analytics] cart item add failed:', error);
            }
          });

          // Trigger refresh in background but don't wait for it
          // This allows optimistic navigation while cart updates
          refreshCart();
          cartActions.setLoading(false);
        } else {
          throw new Error(
            data?.addItemToCart?.error || 'Failed to add item to cart'
          );
        }
      } catch (error) {
        cartActions.setError(
          error instanceof Error ? error.message : 'Failed to add item to cart'
        );
        throw error;
      }
    },
    [addItemMutation, refreshCart]
  );

  const removeFromCart = useCallback(
    async (cartId: string) => {
      try {
        cartActions.setLoading(true);

        const { data } = await removeItemMutation({
          variables: {
            cartItemId: cartId,
          },
        });

        if (data?.removeItemFromCart?.success) {
          const removedItem = cartState.items.find(
            (item) => item._id === cartId
          );
          try {
            cart_item_removed(
              getCartItemAnalyticsProps(removedItem ?? { _id: cartId })
            );
          } catch (error) {
            console.error('[Analytics] cart_item_removed failed:', error);
          }

          // The subscription will handle the update automatically - no need to refresh
          cartActions.setLoading(false);
        } else {
          throw new Error(
            data?.removeItemFromCart?.error || 'Failed to remove item from cart'
          );
        }
      } catch (error) {
        cartActions.setError(
          error instanceof Error
            ? error.message
            : 'Failed to remove item from cart'
        );
        throw error;
      }
    },
    [cartState.items, removeItemMutation]
  );

  const updateQuantity = useCallback(
    async (input: UpdateItemQuantityInput) => {
      try {
        // Subscription will handle the update automatically via change stream
        cartActions.setLoading(true);

        const { data } = await updateQuantityMutation({
          variables: { input },
        });

        if (data?.updateItemQuantity?.success) {
          const previousItem = cartState.items.find(
            (item) => item._id === input.cartItemId
          );
          try {
            const analyticsProps = getCartItemAnalyticsProps(
              previousItem ?? { _id: input.cartItemId },
              input.quantity
            );
            const previousQuantity = previousItem?.quantity ?? 0;
            if (previousQuantity !== input.quantity) {
              cart_item_quantity_changed({
                ...analyticsProps,
                cart_previous_quantity: previousQuantity,
                cart_quantity_change: input.quantity - previousQuantity,
                cart_quantity_change_direction:
                  input.quantity > previousQuantity ? 'increased' : 'decreased',
              });
            }
          } catch (error) {
            console.error('[Analytics] cart quantity change failed:', error);
          }

          // Subscription will update the cart automatically
          // Just set loading to false - subscription handler will update the reactive var
          cartActions.setLoading(false);
        } else {
          // On error, refresh cart to get correct state
          await refreshCart();
          throw new Error(
            data?.updateItemQuantity?.error || 'Failed to update quantity'
          );
        }
      } catch (error) {
        // On error, refresh cart to get correct state
        await refreshCart();
        cartActions.setError(
          error instanceof Error ? error.message : 'Failed to update quantity'
        );
        throw error;
      }
    },
    [cartState.items, updateQuantityMutation, refreshCart]
  );

  const clearCart = useCallback(async () => {
    try {
      cartActions.setLoading(true);

      const { data } = await clearCartMutation();

      if (data?.clearCart?.success) {
        // The subscription will handle the update
        cartActions.clearCart();
      } else {
        throw new Error(data?.clearCart?.error || 'Failed to clear cart');
      }
    } catch (error) {
      cartActions.setError(
        error instanceof Error ? error.message : 'Failed to clear cart'
      );
      throw error;
    }
  }, [clearCartMutation]);

  const applyPromoCode = useCallback(
    async (code: string) => {
      try {
        cartActions.setPromoCodeLoading(true);
        cartActions.setError(null); // Clear any previous errors

        if (code.trim() === '') {
          // Remove promo code
          const { data } = await removePromoMutation();

          if (data?.removePromocode?.success) {
            // The subscription will handle the update automatically - no need to refresh
            cartActions.setPromoCodeLoading(false);
          } else {
            const errorMessage =
              data?.removePromocode?.error || 'Failed to remove promo code';
            cartActions.setError(errorMessage);
            throw new Error(errorMessage);
          }
        } else {
          // Apply promo code
          const { data, errors } = await applyPromoMutation({
            variables: { input: { promocode: code } },
          });

          // Check for GraphQL errors first
          if (errors && errors.length > 0) {
            const errorMessage =
              errors[0]?.message || 'Failed to apply promo code';
            cartActions.setError(errorMessage);
            throw new Error(errorMessage);
          }

          // Check mutation response
          if (data?.applyPromocode?.success) {
            try {
              promo_code_applied({
                entity_id: 'cart',
                entity_type: 'cart',
                cart_promo_code: code,
                cart_item_count: cartState.items.length,
                cart_total_amount: cartState.orderSummary?.total,
              });
              discount_applied({
                entity_id: 'cart',
                entity_type: 'cart',
                cart_promo_code: code,
                cart_discount_amount: cartState.orderSummary?.discount,
                cart_item_count: cartState.items.length,
                cart_total_amount: cartState.orderSummary?.total,
              });
            } catch (error) {
              console.error('[Analytics] promo_code_applied failed:', error);
            }

            // The subscription will handle the update automatically - no need to refresh
            cartActions.setPromoCodeLoading(false);
          } else {
            const errorMessage =
              data?.applyPromocode?.error || 'Failed to apply promo code';
            try {
              promo_code_failed({
                entity_id: 'cart',
                entity_type: 'cart',
                cart_promo_code: code,
                metadata_error_message: errorMessage,
              });
            } catch (error) {
              console.error('[Analytics] promo_code_failed failed:', error);
            }
            cartActions.setError(errorMessage);
            throw new Error(errorMessage);
          }
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Failed to apply promo code';
        if (code.trim() !== '') {
          try {
            promo_code_failed({
              entity_id: 'cart',
              entity_type: 'cart',
              cart_promo_code: code,
              metadata_error_message: errorMessage,
            });
          } catch (analyticsError) {
            console.error(
              '[Analytics] promo_code_failed failed:',
              analyticsError
            );
          }
        }
        cartActions.setError(errorMessage);
        cartActions.setPromoCodeLoading(false);
        throw error;
      }
    },
    [
      applyPromoMutation,
      cartState.items.length,
      cartState.orderSummary?.discount,
      cartState.orderSummary?.total,
      removePromoMutation,
    ]
  );

  // refreshCart function is defined earlier in the file

  // Utility functions
  const getItemById = useCallback(
    (id: string) => {
      return cartState.items.find((item) => item._id === id);
    },
    [cartState.items]
  );

  const getTotalQuantity = useCallback(() => {
    return cartState.items.reduce((total, item) => total + item.quantity, 0);
  }, [cartState.items]);

  // Computed values - memoized to prevent unnecessary recalculations
  const itemCount = useMemo(() => getTotalQuantity(), [cartState.items]);
  // Use order summary total - memoized to prevent re-renders when orderSummary reference is stable
  const total = useMemo(
    () => cartState.orderSummary?.total || 0,
    [cartState.orderSummary?.total]
  );
  const isEmpty = useMemo(
    () => cartState.items.length === 0,
    [cartState.items.length]
  );

  // Consider loading if:
  // 1. Cart is explicitly loading, OR
  // 2. User data is still being loaded, OR
  // 3. User is logged in, we haven't attempted to load yet, and cart is empty
  // This prevents showing empty state before initial load completes
  const isLoading = useMemo(
    () =>
      cartState.loading ||
      userLoading ||
      (!!user?._id && !hasAttemptedInitialLoad && cartState.items.length === 0),
    [
      cartState.loading,
      userLoading,
      user?._id,
      hasAttemptedInitialLoad,
      cartState.items.length,
    ]
  );

  // Memoize return object to prevent unnecessary re-renders
  // Only create new object if relevant state values actually changed
  return useMemo(
    () => ({
      // State
      items: cartState.items,
      orderSummary: cartState.orderSummary,
      itemCount,
      total,
      loading: isLoading,
      error: cartState.error,
      isEmpty,
      appliedPromoCode: cartState.appliedPromoCode,
      subscriptionConnected: cartState.subscriptionConnected,

      // Actions (these are already memoized with useCallback)
      addToCart,
      removeFromCart,
      updateQuantity,
      clearCart,
      applyPromoCode,
      refreshCart,

      // Utilities (these are already memoized with useCallback)
      getItemById,
      getTotalQuantity,
    }),
    [
      cartState.items,
      cartState.orderSummary,
      cartState.error,
      cartState.appliedPromoCode,
      cartState.subscriptionConnected,
      itemCount,
      total,
      isLoading,
      isEmpty,
      addToCart,
      removeFromCart,
      updateQuantity,
      clearCart,
      applyPromoCode,
      refreshCart,
      getItemById,
      getTotalQuantity,
    ]
  );
};
